"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiUrl } from "@/lib/aicheck/api-base";
import { getErrorMessage } from "@/lib/aicheck/error-utils";
import {
  getFreeformReviewResultCacheKey,
  readFreeformReviewResultCache,
  writeFreeformReviewResultCache,
} from "@/lib/aicheck/freeform-result-cache";
import {
  applyFreeformTemplateOverrides,
  createFreeformTemplateRule,
  deleteFreeformTemplateRule,
  readFreeformTemplateOverrides,
  restoreFreeformTemplateDefaults,
  saveFreeformTemplateOverride,
} from "@/lib/aicheck/freeform-template-storage";
import { readNDJSONStream } from "@/lib/ndjson-reader";
import { authService } from "@/services/auth";
import type {
  AggregatedProjectData,
  FreeformCheckResult,
  FreeformCheckTemplate,
  FreeformPartialResult,
  FreeformProgressEvent,
  OutputLanguage,
  ProjectDataSourceDescriptor,
} from "@/types/aicheck";

interface UseFreeformReviewOptions {
  projectId?: number;
  language: OutputLanguage;
  source?: ProjectDataSourceDescriptor;
  initialTemplateId?: string;
  initialPrompt?: string;
}

interface RunFreeformReviewOptions {
  projectId?: number;
  projectData: AggregatedProjectData;
  language: OutputLanguage;
  source?: ProjectDataSourceDescriptor;
}

function progressEventKey(event: FreeformProgressEvent): string | null {
  switch (event.type) {
    case "status":
      return "status";
    case "tool-progress":
      return `tool-progress:${event.itemId ?? "current"}:${
        event.outputIndex ?? "current"
      }`;
    case "reasoning-summary":
      return "reasoning-summary";
    case "api-progress":
    case "partial-summary":
    case "partial-finding":
    case "result":
    case "error":
    case "heartbeat":
      return null;
  }
}

function progressEventSignature(event: FreeformProgressEvent): string {
  switch (event.type) {
    case "status":
      return `${event.status}:${event.message}:${JSON.stringify(
        event.details ?? [],
      )}`;
    case "tool-progress":
      return `${event.eventType}:${event.message}:${event.itemId ?? ""}:${
        event.outputIndex ?? ""
      }:${JSON.stringify(event.details ?? [])}`;
    case "reasoning-summary":
      return `${event.summary}:${event.delta ?? ""}:${JSON.stringify(
        event.details ?? [],
      )}`;
    case "api-progress":
      return `${event.phase}:${event.bytesReceived ?? ""}:${
        event.tokensReceived ?? ""
      }:${event.contentLength ?? ""}`;
    case "partial-summary":
      return event.summary;
    case "partial-finding":
      return `${event.index}:${JSON.stringify(event.finding)}`;
    case "result":
      return event.data.createdAt;
    case "error":
      return event.error;
    case "heartbeat":
      return String(event.ts);
  }
}

function upsertProgressEvent(
  current: FreeformProgressEvent[],
  event: FreeformProgressEvent,
): FreeformProgressEvent[] {
  const key = progressEventKey(event);
  if (!key) return current;
  const existingIndex = current.findIndex(
    (item) => progressEventKey(item) === key,
  );
  if (existingIndex >= 0) {
    const existing = current[existingIndex];
    if (progressEventSignature(existing) === progressEventSignature(event)) {
      return current;
    }
    const next = current.slice();
    next[existingIndex] = event;
    return next;
  }
  return [...current.slice(-12), event];
}

export function useFreeformReview(options: UseFreeformReviewOptions) {
  const [standardTemplates, setStandardTemplates] = useState<
    FreeformCheckTemplate[]
  >([]);
  const [templates, setTemplates] = useState<FreeformCheckTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [stage, setStage] = useState("");
  const [description, setDescription] = useState("");
  const [prompt, setPrompt] = useState("");
  const [progressEvents, setProgressEvents] = useState<FreeformProgressEvent[]>(
    [],
  );
  const [result, setResult] = useState<FreeformCheckResult | null>(null);
  const [partialResult, setPartialResult] =
    useState<FreeformPartialResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [localRuleCount, setLocalRuleCount] = useState(0);
  const [apiProgress, setApiProgress] = useState<{
    phase: "waiting" | "streaming";
    bytesReceived?: number;
    tokensReceived?: number;
    contentLength?: number;
  } | null>(null);
  const [reasoningSummary, setReasoningSummary] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const skipNextRestoreCacheKeyRef = useRef<string | null>(null);

  const selectedTemplate = useMemo(
    () =>
      templates.find((template) => template.id === selectedTemplateId) ?? null,
    [selectedTemplateId, templates],
  );

  const standardTemplateIds = useMemo(
    () => new Set(standardTemplates.map((template) => template.id)),
    [standardTemplates],
  );

  const activeTemplate = useMemo(() => {
    if (!selectedTemplate) return null;
    return {
      ...selectedTemplate,
      stage: stage.trim(),
      description: description.trim(),
      defaultPrompt: prompt,
    };
  }, [description, prompt, selectedTemplate, stage]);

  const hasUnsavedChanges = useMemo(() => {
    if (!selectedTemplate) return false;
    return (
      stage !== selectedTemplate.stage ||
      description !== selectedTemplate.description ||
      prompt !== selectedTemplate.defaultPrompt
    );
  }, [description, prompt, selectedTemplate, stage]);

  const canSaveTemplate =
    hasUnsavedChanges &&
    stage.trim().length > 0 &&
    description.trim().length > 0 &&
    prompt.trim().length > 0;

  const refreshTemplates = useCallback(
    (standard: FreeformCheckTemplate[], preferredTemplateId?: string) => {
      const overrides = readFreeformTemplateOverrides();
      setLocalRuleCount(Object.keys(overrides).length);
      const effective = applyFreeformTemplateOverrides(standard, overrides);
      setTemplates(effective);
      setSelectedTemplateId((current) => {
        const preferred = preferredTemplateId || current;
        if (
          preferred &&
          effective.some((template) => template.id === preferred)
        ) {
          return preferred;
        }
        return effective[0]?.id ?? "";
      });
    },
    [],
  );

  const loadTemplates = useCallback(async () => {
    setIsLoadingTemplates(true);
    setError(null);
    try {
      const token = await authService.getIdToken();
      if (!token) throw new Error("Not authenticated");
      const response = await fetch(apiUrl("/api/aicheck/freeform/templates"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(
          String(data.error ?? `Server error: ${response.status}`),
        );
      }
      const data = (await response.json()) as {
        templates?: FreeformCheckTemplate[];
      };
      const loadedTemplates = data.templates ?? [];
      setStandardTemplates(loadedTemplates);
      refreshTemplates(loadedTemplates, options.initialTemplateId);
    } catch (loadError) {
      setError(getErrorMessage(loadError));
    } finally {
      setIsLoadingTemplates(false);
    }
  }, [options.initialTemplateId, refreshTemplates]);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (options.initialTemplateId) {
      setSelectedTemplateId(options.initialTemplateId);
    }
  }, [options.initialTemplateId]);

  useEffect(() => {
    if (!selectedTemplate) {
      setStage("");
      setDescription("");
      setPrompt("");
      return;
    }
    setStage(selectedTemplate.stage);
    setDescription(selectedTemplate.description);
    if (
      options.initialTemplateId === selectedTemplate.id &&
      options.initialPrompt?.trim()
    ) {
      setPrompt(options.initialPrompt);
      return;
    }
    setPrompt(selectedTemplate.defaultPrompt);
  }, [options.initialPrompt, options.initialTemplateId, selectedTemplate]);

  const activeCacheKey = useMemo(() => {
    if (!options.projectId || !selectedTemplate) return null;
    return getFreeformReviewResultCacheKey({
      projectId: options.projectId,
      language: options.language,
      source: options.source,
    });
  }, [options.language, options.projectId, options.source, selectedTemplate]);

  useEffect(() => {
    if (!activeCacheKey || !selectedTemplate) {
      setResult(null);
      setPartialResult(null);
      setReasoningSummary(null);
      setApiProgress(null);
      setProgressEvents([]);
      return;
    }
    if (isRunning) return;
    if (skipNextRestoreCacheKeyRef.current === activeCacheKey) {
      skipNextRestoreCacheKeyRef.current = null;
      return;
    }

    const cached = readFreeformReviewResultCache({
      projectId: options.projectId,
      language: options.language,
      source: options.source,
    });
    if (!cached) {
      setResult(null);
      setPartialResult(null);
      setReasoningSummary(null);
      setApiProgress(null);
      setProgressEvents([]);
      return;
    }

    setError(null);
    setApiProgress(null);
    setResult(cached.result);
    setPartialResult(null);
    setReasoningSummary(cached.result.reasoningSummary ?? null);
    setProgressEvents([
      {
        type: "status",
        status: "completed",
        message: "Loaded Freeform Review result from cache.",
        createdAt: cached.cachedAt,
      },
    ]);
  }, [
    activeCacheKey,
    isRunning,
    options.language,
    options.projectId,
    options.source,
    selectedTemplate,
  ]);

  const savePrompt = useCallback(() => {
    if (!activeTemplate || !canSaveTemplate) return;
    saveFreeformTemplateOverride(
      activeTemplate,
      prompt,
      !standardTemplateIds.has(activeTemplate.id),
    );
    refreshTemplates(standardTemplates, activeTemplate.id);
  }, [
    activeTemplate,
    canSaveTemplate,
    prompt,
    refreshTemplates,
    standardTemplateIds,
    standardTemplates,
  ]);

  const restoreDefaultTemplates = useCallback(
    (templateIds: string[]) => {
      const restored = restoreFreeformTemplateDefaults(
        templateIds,
        standardTemplates,
      );
      refreshTemplates(standardTemplates, selectedTemplateId);
      return restored;
    },
    [refreshTemplates, selectedTemplateId, standardTemplates],
  );

  const addTemplate = useCallback(() => {
    const id = `local-freeform-${Date.now().toString(36)}`;
    const template: FreeformCheckTemplate = {
      id,
      stage: "New Freeform Review",
      description: "Describe the review focus.",
      defaultPrompt: "Review the Project Data and list practical findings.",
      dataPolicy: "standard",
    };
    createFreeformTemplateRule(template);
    refreshTemplates(standardTemplates, id);
    return id;
  }, [refreshTemplates, standardTemplates]);

  const deleteTemplate = useCallback(
    (templateId: string) => {
      const template = templates.find((item) => item.id === templateId);
      if (!template) return false;
      deleteFreeformTemplateRule(
        template.id,
        standardTemplateIds.has(template.id),
      );
      refreshTemplates(standardTemplates);
      return true;
    },
    [refreshTemplates, standardTemplateIds, standardTemplates, templates],
  );

  const copyTemplate = useCallback(
    (templateId: string, nextStage: string) => {
      const source =
        activeTemplate?.id === templateId
          ? activeTemplate
          : templates.find((item) => item.id === templateId);
      const stageName = nextStage.trim();
      if (!source || !stageName) return false;
      const id = `local-freeform-${Date.now().toString(36)}`;
      createFreeformTemplateRule({
        ...source,
        id,
        stage: stageName,
        dataPolicy: source.dataPolicy ?? "standard",
      });
      refreshTemplates(standardTemplates, id);
      return true;
    },
    [activeTemplate, refreshTemplates, standardTemplates, templates],
  );

  const runReview = useCallback(
    async ({
      projectId,
      projectData,
      language,
      source,
    }: RunFreeformReviewOptions) => {
      if (!activeTemplate) {
        setError("Select a Freeform Review template.");
        return;
      }
      const trimmedPrompt = prompt.trim();
      if (!trimmedPrompt) {
        setError("Prompt is required.");
        return;
      }
      if (!activeTemplate.stage) {
        setError("Stage is required.");
        return;
      }
      if (!activeTemplate.description) {
        setError("Description is required.");
        return;
      }

      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      setIsRunning(true);
      setError(null);
      setResult(null);
      setPartialResult(null);
      setReasoningSummary(null);
      setApiProgress(null);
      setProgressEvents([]);

      try {
        const token = await authService.getIdToken();
        if (!token) throw new Error("Not authenticated");
        const response = await fetch(apiUrl("/api/aicheck/freeform/run"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            mode: "single-project-data",
            projectId,
            projectData,
            templateId: activeTemplate.id,
            template: {
              id: activeTemplate.id,
              stage: activeTemplate.stage,
              description: activeTemplate.description,
            },
            prompt: trimmedPrompt,
            language,
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(
            String(data.error ?? `Server error: ${response.status}`),
          );
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error("No response body");

        for await (const event of readNDJSONStream<FreeformProgressEvent>(
          reader,
        )) {
          if (event.type === "heartbeat") continue;
          if (event.type === "result") {
            setResult(event.data);
            setPartialResult(null);
            writeFreeformReviewResultCache({
              projectId,
              language,
              template: activeTemplate,
              prompt: trimmedPrompt,
              source: source ?? options.source,
              result: event.data,
            });
            // Skip the next cache-restore pass (triggered when `isRunning`
            // flips to false) even if persistence failed, so the result we just
            // displayed is not wiped by an empty cache read.
            skipNextRestoreCacheKeyRef.current = projectId
              ? getFreeformReviewResultCacheKey({
                  projectId,
                  language,
                  source: source ?? options.source,
                })
              : null;
            continue;
          }
          if (event.type === "error") {
            setError(event.error);
            continue;
          }
          if (event.type === "partial-summary") {
            setPartialResult((current) => ({
              templateId: activeTemplate.id,
              templateName: activeTemplate.stage,
              stage: activeTemplate.stage,
              summary: event.summary,
              findings: current?.findings ?? [],
              updatedAt: event.createdAt,
            }));
            continue;
          }
          if (event.type === "partial-finding") {
            setPartialResult((current) => {
              const findings = [...(current?.findings ?? [])];
              findings[event.index] = event.finding;
              return {
                templateId: activeTemplate.id,
                templateName: activeTemplate.stage,
                stage: activeTemplate.stage,
                summary: current?.summary,
                findings: findings.filter(Boolean),
                updatedAt: event.createdAt,
              };
            });
            continue;
          }
          if (event.type === "api-progress") {
            setApiProgress({
              phase: event.phase,
              bytesReceived: event.bytesReceived,
              tokensReceived: event.tokensReceived,
              contentLength: event.contentLength,
            });
          }
          if (event.type === "reasoning-summary") {
            setReasoningSummary(event.summary);
          }
          setProgressEvents((current) => upsertProgressEvent(current, event));
        }
      } catch (runError) {
        if (
          runError instanceof DOMException &&
          runError.name === "AbortError"
        ) {
          setError("Freeform Review was cancelled.");
        } else {
          setError(getErrorMessage(runError));
        }
      } finally {
        setIsRunning(false);
        abortControllerRef.current = null;
      }
    },
    [activeTemplate, options.source, prompt],
  );

  const cancelReview = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  return {
    standardTemplates,
    templates,
    selectedTemplate: activeTemplate,
    selectedTemplateId,
    setSelectedTemplateId,
    stage,
    setStage,
    description,
    setDescription,
    prompt,
    setPrompt,
    progressEvents,
    apiProgress,
    reasoningSummary,
    partialResult,
    result,
    error,
    isLoadingTemplates,
    isRunning,
    localRuleCount,
    hasUnsavedChanges,
    canSaveTemplate,
    savePrompt,
    restoreDefaultTemplates,
    addTemplate,
    copyTemplate,
    deleteTemplate,
    runReview,
    cancelReview,
    reloadTemplates: loadTemplates,
  };
}
